import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CommonDataTable from '../../../components/CommonDataTable';
import { fetchItemInventory, refreshWMSAPI } from '../../../services/item/item';
import { openItemLocationFocus, openHoldInventoryFocus } from '../item-location/itemLocationFocus';

const compareOwnerThenSku = (left, right) => {
  const byOwner = String(left?.owner ?? '').localeCompare(String(right?.owner ?? ''), undefined, { numeric: true, sensitivity: 'base' });
  if (byOwner !== 0) return byOwner;
  return String(left?.item ?? '').localeCompare(String(right?.item ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

const ITEM_SUMMARY_CAT = 'item_summary';

const fetchAllInventoryRows = async (filters = []) => {
  const size = 100;
  let pageNo = 0;
  let collected = [];
  let total = 0;

  do {
    const res = await fetchItemInventory(pageNo, size, filters, ITEM_SUMMARY_CAT);
    const list = Array.isArray(res?.data) ? res.data : [];
    total = Number(res?.totalItems ?? list.length);
    collected = collected.concat(list);
    pageNo += 1;
    if (list.length === 0) break;
  } while (collected.length < total && pageNo < 30);

  return collected;
};

const mapItemSummaryRow = (item, index) => ({
  id: item._id || `${item.owner || 'owner'}-${item.skuId || 'sku'}-${index}`,
  item: item.skuId ?? item.item ?? '',
  owner: item.owner ?? '',
  description: item.description ?? item.itemName ?? '',
  unitOfMeasure: item.uom ?? '',
  facility: item.location || '',
  location: item.location || '',
  facilityName: item.facilityName || '',
  onHand: item.stockOnHand ?? '',
  stockOnHand: item.stockOnHand ?? '',
  available: item.availableQty ?? '',
  onHold: item.onHold ?? '',
  allocated: item.allocated ?? '',
  picked: item.qtyPicked ?? item.picked ?? item.pickedQty ?? '',
  whseId: item.whseId ? String(item.whseId).toUpperCase() : '',
});

const inventoryLinkStyle = {
  color: '#1e71bb',
  cursor: 'pointer',
  textDecoration: 'underline',
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
};

const InventoryLink = ({ value, onOpen }) => {
  if (value == null || String(value).trim() === '') return null;
  return (
    <button
      type="button"
      style={inventoryLinkStyle}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
    >
      {value}
    </button>
  );
};

// Define columns (display names)
const rawColumns = [
  { field: 'owner', headerName: 'Owner', width: 140 },
  {
    field: 'item',
    headerName: 'SKU ID',
    width: 140,
    renderCell: (params) => (
      <InventoryLink
        value={params.value}
        onOpen={() => openItemLocationFocus({ item: params.value })}
      />
    )
  },
  { field: 'description', headerName: 'Description', width: 200 },
  { field: 'unitOfMeasure', headerName: 'UOM', width: 140 },
  { field: 'stockOnHand', headerName: 'Stock On Hand', width: 110, type: 'number' },
  { field: 'allocated', headerName: 'Allocated', width: 110, type: 'number' },
  { field: 'picked', headerName: 'Picked', width: 110, type: 'number' },
  {
    field: 'onHold',
    headerName: 'On Hold',
    width: 120,
    type: 'number',
    renderCell: (params) => {
      const amount = Number(params.value);
      if (!Number.isFinite(amount) || amount <= 0) return params.value ?? '';
      return (
        <InventoryLink
          value={params.value}
          onOpen={() => openHoldInventoryFocus({ item: params.row?.item })}
        />
      );
    },
  },
  { field: 'available', headerName: 'Available', width: 120, type: 'number' },
];

export default function InventoryList() {
  const [tableRows, settableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const sortedCacheRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchHandler = useCallback(async (pageNo, pageSizeNo, filters = [], options = {}) => {
    const filterKey = JSON.stringify(Array.isArray(filters) ? filters : []);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      if (options.refresh || !sortedCacheRef.current || sortedCacheRef.current.filterKey !== filterKey) {
        const list = await fetchAllInventoryRows(filters);
        if (requestId !== requestIdRef.current) return;
        const rows = list.map(mapItemSummaryRow);
        rows.sort(compareOwnerThenSku);
        sortedCacheRef.current = { filterKey, rows };
      }

      const sorted = sortedCacheRef.current.rows;
      const start = pageNo * pageSizeNo;
      settableRows(sorted.slice(start, start + pageSizeNo));
      setTotalCount(sorted.length);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error fetching Item Inventory data:", error);
    }
  }, []);


  useEffect(() => {
    fetchHandler(page, pageSize, appliedFilters);
  }, [fetchHandler, page, pageSize, appliedFilters]);

  const handleDataRefresh = useCallback((payload = {}) => {
    const nextFilters = Array.isArray(payload.filters) ? payload.filters : [];
    const nextSize = Number(payload.pageSize);
    setAppliedFilters(nextFilters);
    if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
    setPage(0);
  }, [pageSize]);


  const handleSyncWms = useCallback(async () => {
    if (syncing) return;

    try {
      setSyncing(true);

      const response = await refreshWMSAPI();

      console.log("WMS Sync Response:", response);

      // Reload the current page with existing filters
      sortedCacheRef.current = null;
      await fetchHandler(page, pageSize, appliedFilters, { refresh: true });

      console.log("Item inventory refreshed successfully");
    } catch (error) {
      console.error("WMS Sync Error:", error);

      alert(
        error?.response?.data?.message ||
        "Failed to sync item inventory from WMS"
      );
    } finally {
      setSyncing(false);
    }
  }, [syncing, fetchHandler, page, pageSize, appliedFilters]);

  const allPossibleColumns = useMemo(() => rawColumns, []);
  const initialVisible = useMemo(() => allPossibleColumns.map(c => c.field), [allPossibleColumns]);

  return (
    <CommonDataTable
      allPossibleColumns={allPossibleColumns}
      initialVisible={initialVisible}
      initialRows={tableRows}
      height={520}
      rowsPerPageOptions={[10, 25, 50, 100]}
      exportImportPath="ItemInventory"
      exportQueryParams={{ cat: ITEM_SUMMARY_CAT }}
      forItems
      forInventory
      enableInlineColumnFilters
      showSyncWms
      onSyncWms={handleSyncWms}
      page={page}
      pageSize={pageSize}
      rowCount={totalCount}
      onPageChange={(newPage) => setPage(newPage)}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setPage(0);
      }}
      onDataRefresh={handleDataRefresh}
      onManualRefresh={(payload) => {
        sortedCacheRef.current = null;
        return fetchHandler(
          payload?.page ?? page,
          payload?.pageSize ?? pageSize,
          payload?.filters ?? appliedFilters,
          { refresh: true }
        );
      }}
    />
  );
}
